// frontend/src/pages/website/blog/HowToGetMoreReviewsAsATradesperson.jsx
import React from "react";
import BlogPostLayout from "./BlogPostLayout";

export default function HowToGetMoreReviewsAsATradesperson() {
    return (
        <BlogPostLayout
            slug="how-to-get-more-reviews-as-a-tradesperson"
            title="How to Get More Reviews as a Tradesperson"
            metaTitle="How to Get More Reviews as a Tradesperson | KonarCard"
            description="Practical advice for UK tradespeople on getting more genuine customer reviews, and why most tradespeople ask at the wrong time."
            pillLabel="Getting more jobs"
            datePublished="2026-04-13"
            readTime="8 min read"
        >
            <p>
                Reviews are the most powerful sales tool a tradesperson can have. More powerful than a website. More powerful than social media. More powerful than any amount of advertising. A potential customer reading five genuine reviews from people like them, homeowners in the same area who had the same type of work done, is already halfway to picking up the phone before they have even spoken to you.
            </p>

            <p>
                Most tradespeople know this. Most of them also have far fewer reviews than they should, given how many satisfied customers they have worked for.
            </p>

            <p>
                The gap between knowing reviews matter and actually collecting them consistently comes down to three things: when you ask, how you ask and how easy you make it for the customer to actually leave one. This post covers all three.
            </p>

            <h2>Why Most Tradespeople Do Not Get Enough Reviews</h2>

            <p>
                It is not because customers are unwilling. Most people are happy to leave a review for a tradesperson who did good work, they just need to be asked at the right moment in the right way.
            </p>

            <p>
                The most common mistakes tradespeople make when it comes to reviews are straightforward.
            </p>

            <p>
                <strong>Asking too late.</strong> Sending a text message asking for a review a week after the job is finished is too late. The customer has moved on. The feeling of satisfaction they had when they saw the finished work has faded. They mean to leave a review but they never quite get around to it.
            </p>

            <p>
                <strong>Not asking at all.</strong> A surprising number of tradespeople never ask directly. They assume satisfied customers will leave reviews on their own. Some do. Most do not, not because they are unhappy but because it simply does not occur to them unless someone asks.
            </p>

            <p>
                <strong>Making it too complicated.</strong> Telling a customer to find you on Google, search for your business name, click the reviews tab and then write something is too many steps. Every extra step loses a percentage of customers who would otherwise have left a review. The easier you make it, the more reviews you get.
            </p>

            <h2>When to Ask, The Right Moment</h2>

            <p>
                The best moment to ask for a review is while you are still on site, the work is finished and the customer can see the result in front of them.
            </p>

            <p>
                That is the peak of their satisfaction. The job looks good. The problem they hired you to solve is solved. They are relieved, pleased and grateful. That feeling is exactly what produces a genuine, enthusiastic review, and it fades quickly once you have left and they have moved on to the next thing in their day.
            </p>

            <p>
                Before you pack up your tools, tap your NFC card to the customer's phone. Their KonarCard profile opens. Show them the review section and ask directly. Not in a pushy way, just honestly. Something like: "If you are happy with the work, a quick review on here really helps me get more jobs like this one." Most customers are glad to help when asked clearly and at the right moment.
            </p>

            <p>
                This works because the effort required from the customer is minimal. The profile is already open on their phone. The review section is right there. They do not have to search for anything or navigate anywhere. They just type a few sentences and hit submit.
            </p>

            <h2>How to Ask, Getting the Wording Right</h2>

            <p>
                The way you ask matters as much as when you ask. There are two approaches that work well for tradespeople.
            </p>

            <p>
                <strong>The direct ask on site.</strong> As described above, tap your card, open the profile, ask face to face while the work is fresh. This is the most effective method and produces the highest conversion rate of satisfied customers into reviewers.
            </p>

            <p>
                <strong>The follow-up message.</strong> For customers where the timing did not work on site, they were in a rush, you were not able to get a moment, a WhatsApp message the same evening or the following morning still catches them close enough to the experience that they are likely to follow through.
            </p>

            <p>Keep the message short and genuine. Something like:</p>

            <p>
                <em>"Hi [name], just checking in to make sure everything is looking good after today. If you are happy with the work, a quick review on my profile would mean a lot, here is the link: [your KonarCard link]. Thanks again for having me."</em>
            </p>

            <p>
                Do not overthink the wording. Customers respond to messages that sound like a real person wrote them, not a template. Keep it conversational and specific to the job where you can.
            </p>

            <h2>What Makes a Good Review</h2>

            <p>
                You cannot write reviews for customers but you can help them write better ones by giving them a small prompt when you ask.
            </p>

            <p>
                Customers who are asked "could you leave a review?" often write something vague like "great service, would recommend." That is fine but it is not particularly persuasive to someone reading it.
            </p>

            <p>
                Customers who are asked "could you leave a review mentioning what the job was and whether you were happy with the finish?" tend to write something much more specific and useful, "Had [name] out to rewire our kitchen extension. Turned up on time, kept the mess to a minimum and the finish was spot on. Would definitely use again."
            </p>

            <p>
                That kind of review does real work. It tells the next customer exactly what trade you are, what type of job you do well and what they can expect from the experience. It answers the questions a potential customer has before they have even asked them.
            </p>

            <p>
                You do not need to script reviews or tell customers what to say. A simple prompt when you ask, "mention what the job was if you can", is enough to lift the quality significantly.
            </p>

            <h2>How Many Reviews Do You Actually Need</h2>

            <p>
                There is no magic number but the research on how customers use reviews gives a clear picture.
            </p>

            <p>
                Customers typically read between three and ten reviews before making a decision. A profile with fewer than five reviews does not give them enough to go on. A profile with ten or more genuine reviews from a variety of customers and job types is enough to establish trust with most people.
            </p>

            <p>
                Getting from zero to ten reviews is the hardest part. Once you have ten, getting to twenty is easier because your profile already looks credible and customers are more willing to add to a profile that already has reviews than to be the first person to leave one.
            </p>

            <p>
                The goal in your first few months is ten reviews from real customers covering a range of job types. After that, making it a consistent habit produces steady growth without any significant effort.
            </p>

            <h2>Handling Negative Reviews</h2>

            <p>
                Most tradespeople dread negative reviews. A bad review feels personal and it is tempting to ignore it, dispute it or respond defensively.
            </p>

            <p>
                The right approach is different and it is one that actually builds trust rather than damaging it.
            </p>

            <p>
                Respond to every negative review calmly and professionally. Acknowledge the customer's experience, explain what happened from your side if relevant and state clearly what you did or would do to resolve it. Keep it short and factual.
            </p>

            <p>
                A tradesperson with forty reviews and one negative review who has responded professionally to the negative one is more credible than a tradesperson with forty perfect reviews and no responses at all. Customers know that not every job goes perfectly. They are looking for evidence that you handle problems well, and a calm, professional response to a negative review provides exactly that evidence.
            </p>

            <h2>Building a Review Habit</h2>

            <p>
                The tradespeople with the strongest review profiles are not doing anything dramatically different from their competitors. They are just consistent.
            </p>

            <p>
                They ask after every job. Not most jobs. Every job. They make it easy by having their profile link ready to share. They follow up the same evening if they did not get a chance on site. Over the course of a year that consistency produces a review profile that sells their work better than any amount of advertising.
            </p>

            <p>
                The best time to start is with the next job you complete. Tap your card, open your profile, ask directly. It takes thirty seconds and the compounding effect over time is significant.
            </p>
        </BlogPostLayout>
    );
}
